import { movieGenres, tvGenres, regions, yearRanges, sortOptions } from "@/mocks/filters";
import type { MediaType } from "@/types/media";

export interface FilterState {
  genres: string[];
  region: string;
  year: string;
  sort: string;
}

interface FilterSidebarProps {
  type: MediaType;
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

export default function FilterSidebar({ type, value, onChange, onReset }: FilterSidebarProps) {
  const genreOptions = type === "movie" ? movieGenres : tvGenres;

  const toggleGenre = (genre: string) => {
    if (genre === "全部") {
      onChange({ ...value, genres: [] });
      return;
    }
    const next = value.genres.includes(genre)
      ? value.genres.filter((g) => g !== genre)
      : [...value.genres, genre];
    onChange({ ...value, genres: next });
  };

  const Chip = ({
    active,
    label,
    onClick,
  }: {
    active: boolean;
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-200/70 text-foreground-400 hover:text-accent-300 hover:bg-background-300/60"
      }`}
    >
      {label}
    </button>
  );

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="lg:sticky lg:top-24 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-xs text-foreground-400 hover:text-accent-300 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置
          </button>
        </div>

        <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-4">
          <h3 className="text-sm font-semibold text-foreground-800 mb-3">类型</h3>
          <div className="flex flex-wrap gap-2">
            <Chip active={value.genres.length === 0} label="全部" onClick={() => toggleGenre("全部")} />
            {genreOptions
              .filter((g) => g !== "全部")
              .map((genre) => (
                <Chip
                  key={genre}
                  active={value.genres.includes(genre)}
                  label={genre}
                  onClick={() => toggleGenre(genre)}
                />
              ))}
          </div>
        </div>

        <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-4">
          <h3 className="text-sm font-semibold text-foreground-800 mb-3">地区</h3>
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <Chip
                key={region}
                active={value.region === region}
                label={region}
                onClick={() => onChange({ ...value, region })}
              />
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-4">
          <h3 className="text-sm font-semibold text-foreground-800 mb-3">年份</h3>
          <div className="flex flex-wrap gap-2">
            {yearRanges.map((year) => (
              <Chip
                key={year.value}
                active={value.year === year.value}
                label={year.label}
                onClick={() => onChange({ ...value, year: year.value })}
              />
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-4">
          <h3 className="text-sm font-semibold text-foreground-800 mb-3">排序</h3>
          <div className="flex flex-col gap-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, sort: option.value })}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors cursor-pointer ${
                  value.sort === option.value
                    ? "bg-secondary-800 text-accent-300"
                    : "text-foreground-400 hover:text-accent-300 hover:bg-background-200/60"
                }`}
              >
                {option.label}
                {value.sort === option.value && <i className="ri-check-line text-accent-400" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}